import React, { useState, useEffect, useRef } from 'react';
import { Camera } from '@mediapipe/camera_utils';
import { 
  initExerciseDetection, 
  detectExercise, 
  setExerciseType, 
  ExerciseType, 
  getRepetitionCount,
  setOnRepetitionCallback,
  setOnFormFeedbackCallback,
  setOnFormScoreCallback,
  getFormScores,
  resetRepetitionCount,
  recordExerciseData,
  setBicepCurlActiveSide,
  resetBicepCurl,
  getRepHistory
} from './ExerciseDetection';
import { calculateSessionMetrics } from '../utils/SessionMetrics';
import PoseVisualizer from '../Exercise/PoseVisualization';
import './ExerciseDashboard.css';


const ExerciseDashboard = () => {
  // State variables
  const [initialized, setInitialized] = useState(false);
  const [exerciseType, setExerciseTypeState] = useState(ExerciseType.OVERHEAD_REACH);
  const [repetitionCount, setRepetitionCount] = useState(0);
  const [formFeedback, setFormFeedback] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [exerciseHistory, setExerciseHistory] = useState([]);
  const [apiInfo, setApiInfo] = useState('');
  const [activeSide, setActiveSide] = useState('right');
  const [cameraError, setCameraError] = useState(null);
  
  const [formScores, setFormScores] = useState({
    overall: 100,
    rangeOfMotion: 0
  });
  
  const [sessionMetrics, setSessionMetrics] = useState({
    formScore: 0,
    rom: { max: 0, average: 0, median: 0, consistency: 0, trend: 0 },
    repCount: 0,
    duration: 0
  });
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseCanvasRef = useRef(null);
  const cameraRef = useRef(null);
  const visualizerRef = useRef(null);
  const timerRef = useRef(null);
  
  const [landmarkColors, setLandmarkColors] = useState({
    main: 'rgba(0, 255, 170, 0.7)',      // Dim teal for non-important landmarks
    secondary: 'rgba(200, 200, 200, 0.6)', // Dim white for face
    highlight: '#ffcc00',                // Bright yellow for target landmarks
    completed: '#33ccff',                // Bright blue for completed reps
    active: '#ff3366'                    // Bright pink for active movement
  });
  const [lastRepTime, setLastRepTime] = useState(0);
  const previousLandmarksRef = useRef(null);
  const smoothingFactorRef = useRef(0.7); // Increase smoothing to reduce jitter
  
  const [devInfo, setDevInfo] = useState({
    fps: 0,
    landmarksDetected: 0,
    cameraResolution: "0x0",
    zoomLevel: 1.0
  });
  
  const fpsRef = useRef(0);
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const repAnimationTimeoutRef = useRef(null);
  
  // Initialize camera and pose detection
  useEffect(() => {
    const initializeDetection = async () => {
      try {
        // Initialize pose detection
        await initExerciseDetection();
        
        // Initialize video stream
        if (videoRef.current) {
          try {
            console.log("Requesting camera access...");
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { 
                width: { ideal: 1280 },
                height: { ideal: 720 }
              },
              audio: false
            }).catch(error => {
              console.error("Camera access error:", error.name, error.message);
              setCameraError(`${error.name}: ${error.message}`);
              alert(`Camera access error: ${error.message}. Please check camera permissions in your browser settings.`);
            });
            
            console.log("Camera access granted, setting up video stream");
            videoRef.current.srcObject = stream;
            
            // Add error handler for video element
            videoRef.current.onerror = (err) => {
              console.error("Video element error:", err);
            };
            
            try {
              await new Promise((resolve, reject) => {
                videoRef.current.onloadedmetadata = () => {
                  console.log("Video metadata loaded, playing video...");
                  // Explicitly play the video with error handling
                  videoRef.current.play()
                    .then(() => {
                      console.log("Video playback started successfully");
                      setDevInfo(prev => ({
                        ...prev,
                        cameraResolution: `${videoRef.current.videoWidth}x${videoRef.current.videoHeight}`
                      }));
                      resolve();
                    })
                    .catch(playErr => {
                      console.error("Error playing video:", playErr);
                      reject(playErr);
                    });
                };
                
                videoRef.current.onerror = (error) => {
                  console.error("Video element error during setup:", error);
                  reject(error);
                };
                
                // Add a timeout in case the metadata event never fires
                setTimeout(() => {
                  reject(new Error("Video metadata loading timed out"));
                }, 5000);
              });
            } catch (videoErr) {
              console.error("Error setting up video playback:", videoErr);
              alert("Error starting video playback. Try refreshing the page.");
            }
            
            console.log("Initializing PoseVisualizer...");
            
            // Initialize the PoseVisualizer with the 3D canvas
            visualizerRef.current = new PoseVisualizer(poseCanvasRef.current);
            
            console.log("Setting up MediaPipe Camera utility...");
            try {
              cameraRef.current = new Camera(videoRef.current, {
                onFrame: async () => {
                  try {
                    await onFrameCallback();
                  } catch (err) {
                    console.error("Error in onFrame callback:", err);
                  }
                },
                width: 1280,
                height: 720,
                facingMode: 'user',
                fps: 30 // Lower FPS for better performance
              });
              
              console.log("Starting camera processing...");
              await cameraRef.current.start();
              console.log("Camera processing started successfully");
            } catch (cameraErr) {
              console.error("Error initializing or starting the camera:", cameraErr);
              alert("Error starting camera processing. Please check console for details.");
            }
            
            setOnRepetitionCallback(exerciseType, (count, feedback, exerciseState) => {
              setRepetitionCount(count);
              setLastRepTime(performance.now());
              
              if (feedback) {
                setFormFeedback(feedback);
              }
              
              if (isRecording) {
                recordExerciseData(
                  exerciseType,
                  visualizerRef.current.pointMeshes,
                  feedback,
                  { count, timestamp: new Date().toISOString() }
                );
              }
            });
            
            setOnFormFeedbackCallback(exerciseType, (feedback) => {
              setFormFeedback(feedback);
            });
            
            setOnFormScoreCallback(exerciseType, (scores) => {
              setFormScores(scores);
            });
            
            setInitialized(true);
          } catch (error) {
            console.error("Error initializing detection:", error);
          }
        }
      } catch (error) {
        console.error("Error initializing detection:", error);
      }
      
      timerRef.current = setInterval(() => {
        setSessionDuration(prev => prev + 1);
      }, 1000);
      
      return () => {
        if (cameraRef.current) {
          cameraRef.current.stop();
        }
        
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        
        if (videoRef.current && videoRef.current.srcObject) {
          const tracks = videoRef.current.srcObject.getTracks();
          tracks.forEach(track => track.stop());
        }
      };
    };
    
    initializeDetection();
  }, []);
  
  useEffect(() => {
    if (initialized) {
      setExerciseType(exerciseType);
      
      // Reset repetition count
      if (exerciseType === ExerciseType.BICEP_CURL) {
        resetBicepCurl();
        // Set active side based on state
        setBicepCurlActiveSide(activeSide);
      } else {
        resetRepetitionCount(exerciseType);
      }
      
      setRepetitionCount(0);
      setFormFeedback('');
      
      setFormScores({
        overall: 100,
        rangeOfMotion: 0
      });
      
      setOnRepetitionCallback(exerciseType, (count, feedback, exerciseState) => {
        setRepetitionCount(count);
        setLastRepTime(performance.now());
        
        if (feedback) {
          setFormFeedback(feedback);
        }
        
        // Update session metrics when rep completes
        const repHistory = getRepHistory(exerciseType);
        if (repHistory && repHistory.length > 0) {
          const metrics = calculateSessionMetrics(repHistory);
          setSessionMetrics(metrics);
        }
        
        setExerciseHistory(prev => [
          ...prev,
          {
            type: exerciseType,
            timestamp: new Date().toISOString(),
            count: count,
            formScore: exerciseState?.formScore || 100
          }
        ]);
        
        if (isRecording) {
          recordExerciseData(
            exerciseType,
            visualizerRef.current.pointMeshes,
            feedback,
            { count, timestamp: new Date().toISOString() }
          );
        }
      });
      
      setOnFormFeedbackCallback(exerciseType, (feedback) => {
        setFormFeedback(feedback);
      });
      
      setOnFormScoreCallback(exerciseType, (scores) => {
        setFormScores(scores);
      });
    }
  }, [exerciseType, initialized, isRecording, activeSide]);
  
  useEffect(() => {
    const handleResize = () => {
      if (visualizerRef.current && poseCanvasRef.current) {
        visualizerRef.current.resize(
          poseCanvasRef.current.clientWidth,
          poseCanvasRef.current.clientHeight
        );
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const drawLandmarks = (canvas, landmarks) => {
    if (!landmarks || landmarks.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.clearRect(0, 0, width, height);
    
    const connections = [
      // Torso
      [11, 12], // shoulders
      [12, 24], // right shoulder to right hip
      [11, 23], // left shoulder to left hip
      [23, 24], // hips
      
      // Arms
      [11, 13], // left shoulder to left elbow
      [13, 15], // left elbow to left wrist
      [12, 14], // right shoulder to right elbow
      [14, 16], // right elbow to right wrist
      
      // Legs
      [23, 25], // left hip to left knee
      [25, 27], // left knee to left ankle
      [24, 26], // right hip to right knee
      [26, 28], // right knee to right ankle
    ];
    
    const smoothedLandmarks = smoothLandmarks(landmarks);
    
    const singleColor = '#00FF99';
    ctx.lineWidth = 5;
    ctx.strokeStyle = singleColor;
    ctx.shadowColor = singleColor;
    ctx.shadowBlur = 10;
    
    connections.forEach(([i, j]) => {
      if (smoothedLandmarks[i] && smoothedLandmarks[j]) {
        const x1 = smoothedLandmarks[i].x * width;
        const y1 = smoothedLandmarks[i].y * height;
        const x2 = smoothedLandmarks[j].x * width;
        const y2 = smoothedLandmarks[j].y * height;
        
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    });
    
    smoothedLandmarks.forEach((landmark, index) => {
      const x = landmark.x * width;
      const y = landmark.y * height;
      
      if (landmark.visibility && landmark.visibility < 0.5) return;
      
      ctx.save();
      
      const isKeyLandmark = [0, 11, 12, 13, 14, 15, 16, 23, 24].includes(index);
      
      ctx.fillStyle = singleColor;
      
      ctx.shadowColor = singleColor;
      ctx.shadowBlur = isKeyLandmark ? 15 : 10;
      
      const size = isKeyLandmark ? 10 : 6;
      
      ctx.beginPath();
      ctx.arc(x, y, size, 0, 2 * Math.PI);
      ctx.fill();
      
      if (isKeyLandmark) {
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, size + 2, 0, 2 * Math.PI);
        ctx.stroke();
      }
      
      ctx.restore();
      
      if (isKeyLandmark) {
        const landmarkNames = {
          0: 'Nose', 11: 'L.Shldr', 12: 'R.Shldr',
          13: 'L.Elbow', 14: 'R.Elbow', 15: 'L.Wrist',
          16: 'R.Wrist', 23: 'L.Hip', 24: 'R.Hip'
        };
        
        if (landmarkNames[index]) {
          ctx.save();
          ctx.scale(-1, 1);
          ctx.fillStyle = 'white';
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.shadowColor = 'black';
          ctx.shadowBlur = 4;
          ctx.fillText(landmarkNames[index], -x, y - size - 10);
          ctx.restore();
        }
      }
    });
  };

  const smoothLandmarks = (currentLandmarks) => {
    if (!previousLandmarksRef.current) {
      previousLandmarksRef.current = JSON.parse(JSON.stringify(currentLandmarks));
      return currentLandmarks;
    }
    
    const factor = smoothingFactorRef.current;
    
    const smoothed = currentLandmarks.map((landmark, i) => {
      if (i < previousLandmarksRef.current.length) {
        const prev = previousLandmarksRef.current[i];
        
        return {
          x: prev.x * factor + landmark.x * (1 - factor),
          y: prev.y * factor + landmark.y * (1 - factor),
          z: prev.z ? (prev.z * factor + landmark.z * (1 - factor)) : landmark.z,
          visibility: landmark.visibility
        };
      }
      return landmark;
    });
    
    previousLandmarksRef.current = JSON.parse(JSON.stringify(smoothed));
    
    return smoothed;
  };

  const drawConnections = (ctx, landmarks) => {
    const width = ctx.canvas.width;
    const height = ctx.canvas.height;
    
    const connections = [
      // Face connections
      [0, 1], [1, 2], [2, 3], [3, 7], [0, 4], [4, 5], [5, 6], [6, 8],
      // Torso connections
      [11, 12], [11, 23], [12, 24], [23, 24],
      // Left arm
      [11, 13], [13, 15], [15, 17], [15, 19], [17, 19],
      // Right arm
      [12, 14], [14, 16], [16, 18], [16, 20], [18, 20],
      // Left leg
      [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
      // Right leg
      [24, 26], [26, 28], [28, 30], [28, 32], [30, 32]
    ];
    
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    
    connections.forEach(([i, j]) => {
      if (i < landmarks.length && j < landmarks.length) {
        const landmark1 = landmarks[i];
        const landmark2 = landmarks[j];
        
        if ((landmark1.visibility && landmark1.visibility < 0.1) || 
            (landmark2.visibility && landmark2.visibility < 0.1)) {
          return;
        }
        
        const x1 = landmark1.x * width;
        const y1 = landmark1.y * height;
        const x2 = landmark2.x * width;
        const y2 = landmark2.y * height;
        
        let color;
        // Face connections
        if (i <= 10 && j <= 10) {
          color = landmarkColors.secondary;
        }
        // Arm connections
        else if ([11, 12, 13, 14, 15, 16, 17, 18, 19, 20].includes(i) || 
                 [11, 12, 13, 14, 15, 16, 17, 18, 19, 20].includes(j)) {
          color = landmarkColors.highlight;
        }
        // Leg connections
        else if ([23, 24, 25, 26, 27, 28, 29, 30, 31, 32].includes(i) || 
                 [23, 24, 25, 26, 27, 28, 29, 30, 31, 32].includes(j)) {
          color = landmarkColors.main;
        }
        // Torso connections
        else {
          color = landmarkColors.main;
        }
        
        const timeSinceLastRep = performance.now() - lastRepTime;
        if (timeSinceLastRep < 500) {
          color = landmarkColors.completed;
        }
        
        const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, color);
        
        ctx.strokeStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    });
  };
  
  const handleExerciseChange = (e) => {
    const newType = e.target.value;
    setExerciseTypeState(newType);
    
    // Reset active side to right when changing exercises
    setActiveSide('right');
    
    // API implementation info based on selected exercise
    setApiInfo(getApiImplementationInfo(newType));
  };
  
  const toggleRecording = () => {
    setIsRecording(!isRecording);
  };
  
  const handleReset = () => {
    if (exerciseType === ExerciseType.BICEP_CURL) {
      resetBicepCurl();
    } else {
      resetRepetitionCount(exerciseType);
    }
    setRepetitionCount(0);
    setSessionMetrics({
      formScore: 0,
      rom: { max: 0, average: 0, median: 0, consistency: 0, trend: 0 },
      repCount: 0,
      duration: 0
    });
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const getApiImplementationInfo = (exerciseType) => {
    const commonSetup = `
    // Common setup for all exercises:
    await initExerciseDetection();
    setExerciseType(ExerciseType.${exerciseType.toUpperCase()});
    `;

    switch (exerciseType) {
      case ExerciseType.OVERHEAD_REACH:
        return `${commonSetup}
    // Implementation for Overhead Reaching:
    setOnRepetitionCallback(ExerciseType.OVERHEAD_REACH, (count, feedback) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - When player raises arm above head, character jumps or reaches for item
      // - Each repetition could collect overhead items in a platformer
      
      myGame.playerJump();
      myGame.collectOverheadItem();
    });`;

      case ExerciseType.ARM_ABDUCTION:
        return `${commonSetup}
    // Implementation for Arm Abduction:
    setOnRepetitionCallback(ExerciseType.ARM_ABDUCTION, (count, feedback) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Player extends arms outward to control character wingspan
      // - Each repetition could increase shield size or defensive barrier
      
      myGame.extendWings();
      myGame.activateShield();
    });`;

      case ExerciseType.CROSS_BODY_PUNCH:
        return `${commonSetup}
    // Implementation for Cross-Body Punches:
    setOnRepetitionCallback(ExerciseType.CROSS_BODY_PUNCH, (count, feedback) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each alternating punch triggers attack in fighting game
      // - Direction of punch (left/right) determines attack direction
      
      const punchDirection = count % 2 === 0 ? 'right' : 'left';
      myGame.punchAttack(punchDirection);
    });`;

      case ExerciseType.CORE_TWIST:
        return `${commonSetup}
    // Implementation for Core Twists:
    setOnRepetitionCallback(ExerciseType.CORE_TWIST, (count, feedback) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each twist steers a vehicle or character in racing/flying game
      // - Rotation angle could determine steering intensity
      
      const direction = count % 2 === 0 ? 'right' : 'left';
      myGame.steerVehicle(direction);
    });`;

      case ExerciseType.LATERAL_CORE_FLEXION:
        return `${commonSetup}
    // Implementation for Lateral Core Flexion:
    setOnRepetitionCallback(ExerciseType.LATERAL_CORE_FLEXION, (count, feedback) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each side bend dodges obstacles in runner game
      // - Direction of bend determines dodge direction
      
      const dodgeDirection = count % 2 === 0 ? 'right' : 'left';
      myGame.dodgeObstacle(dodgeDirection);
    });`;

      case ExerciseType.ROW:
        return `${commonSetup}
    // Implementation for Rowing (Bilateral):
    setOnRepetitionCallback(ExerciseType.ROW, (count, feedback, exerciseState) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each row could propel a boat or character forward
      // - Power and speed of movement based on form score
      
      const power = exerciseState.formScore / 100; // 0-1 power scale
      myGame.rowBoat(power);
      myGame.moveForward(power * 5); // Move 0-5 units based on form quality
    });`;

      case ExerciseType.BICEP_CURL:
        return `${commonSetup}
    // Implementation for Bicep Curls:
    setOnRepetitionCallback(ExerciseType.BICEP_CURL, (count, feedback, exerciseState) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each curl lifts a virtual weight or powers up character
      // - Alternating arms could control different game elements
      
      const side = exerciseState.activeSide; // 'left' or 'right'
      const power = exerciseState.contractionScore / 100; // 0-1 power scale
      
      myGame.liftObject(side, power);
      myGame.increaseStrength(power * 10); // Add 0-10 strength points
    });`;

      case ExerciseType.SEATED_KNEE_EXTENSIONS:
        return `${commonSetup}
    // Implementation for Seated Knee Extensions:
    setOnRepetitionCallback(ExerciseType.SEATED_KNEE_EXTENSIONS, (count, feedback, exerciseState) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each extension could kick or launch objects in game
      // - Extension quality could determine distance or power
      
      const side = exerciseState.activeLeg; // 'left' or 'right'
      const power = exerciseState.extensionScore / 100; // 0-1 power scale
      
      myGame.kickBall(side, power);
      myGame.launchProjectile(side, power * 20); // Launch distance 0-20 units
    });`;

      case ExerciseType.SEATED_MARCHES:
        return `${commonSetup}
    // Implementation for Seated Marches:
    setOnRepetitionCallback(ExerciseType.SEATED_MARCHES, (count, feedback, exerciseState) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each knee raise moves character forward in rhythm game
      // - Height of raise could determine jump height or obstacle clearance
      
      const side = exerciseState.activeLeg; // 'left' or 'right'
      const height = exerciseState.heightScore / 100; // 0-1 height scale
      
      myGame.step(side);
      myGame.jumpObstacle(height * 3); // Jump height 0-3 units
    });`;

      case ExerciseType.SHOULDER_ELEVATION:
        return `${commonSetup}
    // Implementation for Shoulder Elevation:
    setOnRepetitionCallback(ExerciseType.SHOULDER_ELEVATION, (count, feedback, exerciseState) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each shoulder raise could lift character or objects in game
      // - Height of elevation could control floating or levitation mechanics
      
      const height = exerciseState.heightScore / 100; // 0-1 height scale
      const symmetry = exerciseState.symmetryScore / 100; // 0-1 balance scale
      
      myGame.levitate(height * 5); // Lift 0-5 units
      myGame.balanceFloat(symmetry); // 0-1 stability factor
    });`;

      case ExerciseType.ARM_ABDUCTION_FRONT:
        return `${commonSetup}
    // Implementation for Arm Abduction (Front):
    setOnRepetitionCallback(ExerciseType.ARM_ABDUCTION_FRONT, (count, feedback, exerciseState) => {
      console.log(\`Repetition #\${count} detected!\`);
      
      // Example game implementation:
      // - Each forward raise could push objects or create force field
      // - Height and symmetry could determine power and area of effect
      
      const height = exerciseState.heightScore / 100; // 0-1 height scale
      const power = exerciseState.extensionScore / 100; // 0-1 power scale
      
      myGame.pushObjects(power * 10); // Push force 0-10 units
      myGame.createForceField(height * 5); // Field radius 0-5 units
    });`;

      default:
        return commonSetup;
    }
  };

  useEffect(() => {
    setApiInfo(getApiImplementationInfo(exerciseType));
  }, []);
  
  useEffect(() => {
    const handleRepAnimation = () => {
      const timeSinceLastRep = performance.now() - lastRepTime;
      if (timeSinceLastRep < 1000) {
        const cameraFeed = document.querySelector('.camera-feed');
        if (cameraFeed) {
          cameraFeed.classList.add('rep-completed');
          // Clear any existing timeout
          if (repAnimationTimeoutRef.current) {
            clearTimeout(repAnimationTimeoutRef.current);
          }
          repAnimationTimeoutRef.current = setTimeout(() => {
            cameraFeed.classList.remove('rep-completed');
            repAnimationTimeoutRef.current = null;
          }, 1000);
        }
      }
    };
    
    handleRepAnimation();
    
    // Cleanup timeout on unmount or when effect re-runs
    return () => {
      if (repAnimationTimeoutRef.current) {
        clearTimeout(repAnimationTimeoutRef.current);
        repAnimationTimeoutRef.current = null;
      }
    };
  }, [lastRepTime]);
  
  useEffect(() => {
    const updateDevInfoInterval = setInterval(() => {
      if (visualizerRef.current) {
        setDevInfo(prev => ({
          ...prev,
          fps: Math.round(fpsRef.current),
          zoomLevel: visualizerRef.current.zoomLevel.toFixed(1)
        }));
      }
    }, 500);
    
    return () => clearInterval(updateDevInfoInterval);
  }, []);
  
  const onFrameCallback = async () => {
    frameCountRef.current++;
    const now = performance.now();
    const elapsed = now - lastTimeRef.current;
    
    if (elapsed >= 1000) {
      fpsRef.current = frameCountRef.current / (elapsed / 1000);
      frameCountRef.current = 0;
      lastTimeRef.current = now;
    }
    
    const timestamp = performance.now();
    const result = await detectExercise(videoRef.current, timestamp);
    
    if (result && result.worldLandmarks?.[0]) {
      setDevInfo(prev => ({
        ...prev,
        landmarksDetected: result.worldLandmarks[0].length
      }));
      
      if (result.formScores) {
        setFormScores(result.formScores);
      }
      
      if (result.isRepCompleted) {
        setLastRepTime(performance.now());
      }
      
      visualizerRef.current.updatePose(result.worldLandmarks[0]);
      drawLandmarks(canvasRef.current, result.landmarks[0]);
    }
  };
  
  const DevInfoPanel = () => (
    <div className="dev-info-panel">
      <div className="dev-info-item">FPS: {devInfo.fps}</div>
      <div className="dev-info-item">Landmarks: {devInfo.landmarksDetected}</div>
      <div className="dev-info-item">Camera: {devInfo.cameraResolution}</div>
      <div className="dev-info-item">Zoom: {devInfo.zoomLevel}x</div>
    </div>
  );
  
  const FormScoreCard = ({ scores }) => {
    const getScoreColor = (score) => {
      if (score >= 80) return '#4CAF50'; // Green
      if (score >= 60) return '#FFC107'; // Yellow
      return '#F44336'; // Red
    };
    
    const getROMDisplay = (rom) => {
      if (rom >= 90) return 'Full range';
      if (rom >= 67) return '¾ range';
      if (rom >= 45) return '½ range';
      if (rom >= 22) return '¼ range';
      return 'Limited';
    };

    return (
      <div className="form-score-card">
        <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666', fontWeight: 'bold' }}>
          CURRENT REP
        </div>
        {/* Form Score */}
        <div className="score-item">
          <div className="score-label">Form Score</div>
          <div
            className="score-value-large"
            style={{ color: getScoreColor(scores.overall) }}
          >
            {Math.round(scores.overall)}
          </div>
          <div className="score-bar-container">
            <div
              className="score-bar-fill"
              style={{
                width: `${Math.min(100, scores.overall)}%`,
                backgroundColor: getScoreColor(scores.overall)
              }}
            />
          </div>
        </div>

        {/* Range of Motion */}
        <div className="score-item">
          <div className="score-label">Range of Motion</div>
          <div
            className="score-value-large"
            style={{ color: getScoreColor(scores.rangeOfMotion) }}
          >
            {Math.round(scores.rangeOfMotion)}%
          </div>
          <div className="score-bar-container">
            <div
              className="score-bar-fill"
              style={{
                width: `${Math.min(100, scores.rangeOfMotion)}%`,
                backgroundColor: getScoreColor(scores.rangeOfMotion)
              }}
            />
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '8px', fontWeight: '500' }}>
            {getROMDisplay(scores.rangeOfMotion)}
          </div>
        </div>
      </div>
    );
  };
  
  const SessionMetricsCard = ({ metrics }) => {
    const getScoreColor = (score) => {
      if (score >= 80) return '#4CAF50';
      if (score >= 60) return '#FFC107';
      return '#F44336';
    };
    
    const getTrendIcon = (trend) => {
      if (trend > 2) return '📈';
      if (trend < -2) return '📉';
      return '➡️';
    };
    
    const getROMDisplay = (avgROM) => {
      if (avgROM >= 90) return 'Full range';
      if (avgROM >= 67) return '¾ range';
      if (avgROM >= 45) return '½ range';
      if (avgROM >= 22) return '¼ range';
      return 'Limited';
    };
    
    // Calculate peak form score from rep history
    const repHistory = getRepHistory(exerciseType);
    const peakFormScore = repHistory && repHistory.length > 0 
      ? Math.max(...repHistory.map(r => r.formScore))
      : 0;

    return (
      <div className="form-score-card">
        <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666', fontWeight: 'bold' }}>
          SESSION SUMMARY ({metrics.repCount} reps)
        </div>
        
        {/* Session Form Score */}
        <div className="score-item">
          <div className="score-label">Avg Form Score</div>
          <div
            className="score-value-large"
            style={{ color: getScoreColor(metrics.formScore) }}
          >
            {Math.round(metrics.formScore)}
          </div>
          <div className="score-bar-container">
            <div
              className="score-bar-fill"
              style={{
                width: `${Math.min(100, metrics.formScore)}%`,
                backgroundColor: getScoreColor(metrics.formScore)
              }}
            />
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
            Peak: {Math.round(peakFormScore)} • Reflects best rep achieved
          </div>
        </div>

        {/* Session ROM */}
        <div className="score-item">
          <div className="score-label">Avg ROM</div>
          <div
            className="score-value-large"
            style={{ color: getScoreColor(metrics.rom.average) }}
          >
            {Math.round(metrics.rom.average)}%
          </div>
          <div className="score-bar-container">
            <div
              className="score-bar-fill"
              style={{
                width: `${Math.min(100, metrics.rom.average)}%`,
                backgroundColor: getScoreColor(metrics.rom.average)
              }}
            />
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
            {getROMDisplay(metrics.rom.average)} • Max: {Math.round(metrics.rom.max)}%
          </div>
        </div>
        
        {/* ROM Stats */}
        <div style={{ marginTop: '15px', fontSize: '12px', color: '#666', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <div style={{ fontWeight: 'bold' }}>Consistency</div>
            <div style={{ fontSize: '16px', color: getScoreColor(metrics.rom.consistency) }}>
              {Math.round(metrics.rom.consistency)}%
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 'bold' }}>Trend</div>
            <div style={{ fontSize: '16px' }}>
              {getTrendIcon(metrics.rom.trend)} {metrics.rom.trend > 0 ? '+' : ''}{metrics.rom.trend.toFixed(1)}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const toggleActiveSide = () => {
    const newSide = activeSide === 'right' ? 'left' : 'right';
    setActiveSide(newSide);
    setBicepCurlActiveSide(newSide);
  };
  
  return (
    <div className="exercise-dashboard-root">
      <div className="exercise-dashboard">
        <div className="dashboard-header">
          <h1>Exercise Tracking Dashboard</h1>
          <div className="exercise-controls">
            <select value={exerciseType} onChange={handleExerciseChange}>
              <option value={ExerciseType.OVERHEAD_REACH}>Overhead Reach</option>
              <option value={ExerciseType.ARM_ABDUCTION}>Arm Abduction</option>
              <option value={ExerciseType.CROSS_BODY_PUNCH}>Cross Body Punch</option>
              <option value={ExerciseType.CORE_TWIST}>Core Twist</option>
              <option value={ExerciseType.LATERAL_CORE_FLEXION}>Lateral Core Flexion</option>
              <option value={ExerciseType.ROW}>Row (Bilateral)</option>
              <option value={ExerciseType.BICEP_CURL}>Bicep Curl (Unilateral)</option>
              <option value={ExerciseType.SEATED_KNEE_EXTENSIONS}>Seated Knee Extensions</option>
              <option value={ExerciseType.SEATED_MARCHES}>Seated Marches (Alternating Legs)</option>
              <option value={ExerciseType.SHOULDER_ELEVATION}>Shoulder Elevation (Bilateral)</option>
              <option value={ExerciseType.ARM_ABDUCTION_FRONT}>Arm Abduction (Front)</option>
            </select>
            <button onClick={handleReset}>Reset Count</button>
            {exerciseType === ExerciseType.BICEP_CURL && (
              <button onClick={toggleActiveSide}>
                Active Arm: {activeSide.toUpperCase()}
              </button>
            )}
            <button 
              className={isRecording ? 'recording' : ''} 
              onClick={toggleRecording}
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </button>
          </div>
        </div>
        
        <div className="dashboard-content">
          <div className="widget-container">
            <div className="widget-title">Live Video Feed</div>
            <div className="camera-feed">
              {cameraError ? (
                <div className="camera-error" style={{ 
                  padding: '20px', 
                  backgroundColor: '#ffdddd', 
                  color: '#d82c2c',
                  borderRadius: '4px',
                  textAlign: 'center',
                  height: '90%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <h2>Camera Error</h2>
                  <p>{cameraError}</p>
                  <p>Please ensure that:</p>
                  <ul style={{ textAlign: 'left', maxWidth: '80%', margin: '0 auto' }}>
                    <li>You've granted camera permissions to this page</li>
                    <li>No other application is using your camera</li>
                    <li>Your camera is properly connected and working</li>
                  </ul>
                  <button 
                    onClick={() => window.location.reload()} 
                    style={{ 
                      margin: '20px auto', 
                      padding: '10px 20px', 
                      backgroundColor: '#4CAF50', 
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Reload Page
                  </button>
                </div>
              ) : (
                <>
                  <video 
                    ref={videoRef} 
                    playsInline
                    autoPlay
                    muted
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: 'scaleX(-1)' // Mirror the video horizontally
                    }}
                    className="camera-video"
                  />
                  <canvas 
                    ref={canvasRef} 
                    width="1280" 
                    height="720"
                    className="landmarks-canvas"
                  />
                </>
              )}
            </div>
          </div>
          
          <div className="widget-container">
            <div className="widget-title">3D Pose Visualization</div>
            <div className="pose-visualization">
              <canvas
                ref={poseCanvasRef}
                width="600"
                height="600"
                className="pose-canvas"
              />
            </div>
          </div>
          
          <div className="widget-container">
            <div className="widget-title">Exercise Metrics</div>
            <div className="metrics-card">
              <h2>Exercise: {exerciseType.replace(/_/g, ' ').toUpperCase()}</h2>
              <div className="metric">
                <span className="metric-label">Repetition Count:</span>
                <span className="metric-value">{repetitionCount}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Session Duration:</span>
                <span className="metric-value">{formatTime(sessionDuration)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Form Feedback:</span>
                <span className="metric-value feedback">{formFeedback || "Good form"}</span>
              </div>
              <div className="recording-indicator">
                {isRecording && <span className="recording-dot"></span>}
                {isRecording ? 'Recording exercise data...' : 'Exercise data recording paused'}
              </div>
            </div>
          </div>
          
          <div className="widget-container">
            <div className="widget-title">Form Analysis</div>
            <FormScoreCard scores={formScores} />
          </div>
          
          {repetitionCount > 0 && (
            <div className="widget-container">
              <div className="widget-title">Session Analytics</div>
              <SessionMetricsCard metrics={sessionMetrics} />
            </div>
          )}
        </div>
        
        <DevInfoPanel />
        
        <div className="api-info-section">
          <h3>Developer API Implementation</h3>
          <div className="api-code-container">
            <pre className="api-code">
              <code>{apiInfo}</code>
            </pre>
          </div>
          <div className="api-info-help">
            <p>
              Copy this code to implement the exercise detection in your game. 
              See the full API documentation for more options.
            </p>
          </div>
        </div>
        
        <div className="history-section">
          <h2>Exercise History</h2>
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Exercise</th>
                  <th>Repetition Count</th>
                  <th>Form Score</th>
                </tr>
              </thead>
              <tbody>
                {exerciseHistory.length === 0 ? (
                  <tr>
                    <td colSpan="4">No exercise data yet. Start exercising!</td>
                  </tr>
                ) : (
                  exerciseHistory.map((entry, index) => (
                    <tr key={index}>
                      <td>{new Date(entry.timestamp).toLocaleTimeString()}</td>
                      <td>{entry.type.replace(/_/g, ' ')}</td>
                      <td>{entry.count}</td>
                      <td>{entry.formScore || 'N/A'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseDashboard;